/**
 * Svátková okna — ČISTÉ funkce nad daty, která chodí z administrace.
 *
 * Do doladění vize struktura-webu (řez 1) tu ležela konfigurace `HOLIDAY_WINDOWS`
 * s okny opsanými z vyhlášek MŠMT a doplnění dalšího školního roku znamenalo nasazení.
 * Okna se přesunula do tabulky `holidays` (`packages/db/src/schema/holidays.ts`),
 * protože nejsou úřední fakt, ale obchodní rozhodnutí: operátor je smí posunout mimo
 * samotný svátek (svátek v pondělí ⇒ lidé jedou už od pátku).
 *
 * Zároveň PŘESTALY být filtrem (řez 2). Tlačítko svátku v panelu vyplní `dateFrom`/`dateTo`
 * v sekci Termín a dál platí obyčejná datová dimenze („zájezd se do okna vejde CELÝ"
 * + tolerance ±3 dny). Proto tu po řezu 2 nezůstalo nic o překryvu: `rangeOverlapsWindows`
 * ani `futureHolidayWindows` už neměly konzumenta a byly by to jen nejbližší po ruce ležící
 * helpery se sémantikou, kterou celý řez ruší. Kdo je vrátí, vrátí i dvojí pravidlo termínu.
 */

export interface HolidayWindow {
  /** První den okna, ISO YYYY-MM-DD, včetně. */
  from: string;
  /** Poslední den okna, ISO YYYY-MM-DD, včetně. */
  to: string;
}

/**
 * Nejbližší okno, které ještě neproběhlo celé (`to >= today`), nebo `null`, když jsou
 * všechna v minulosti (typicky svátek, jehož poslední nakonfigurovaný rok už uplynul —
 * pak je na operátorovi doplnit další).
 *
 * Vybírá se MINIMUM, ne první prvek pole: pořadí oken v administraci nikdo nehlídá
 * a chronologie se ručním přidáním roku snadno rozhodí.
 */
export function nextHolidayWindow(
  windows: readonly HolidayWindow[],
  today: string,
): HolidayWindow | null {
  let best: HolidayWindow | null = null;
  for (const w of windows) {
    if (!w || typeof w.from !== "string" || typeof w.to !== "string") continue;
    if (w.to < today) continue;
    if (!best || w.from < best.from) best = w;
  }
  return best;
}

/** „25. 3." — den a měsíc bez roku; rok dopisuje až `formatHolidayWindow`. */
function formatDayMonth(iso: string): string {
  const [, month, day] = iso.split("-");
  return `${Number(day)}. ${Number(month)}.`;
}

/** „25. 3. 2027" — český číselný tvar bez závislosti na `Intl` a časovém pásmu. */
function formatHolidayDay(iso: string): string {
  return `${formatDayMonth(iso)} ${iso.split("-")[0]}`;
}

/**
 * Popisek okna u svátkového tlačítka (vize E7) — VŽDY s rokem, protože „25.–29. 3."
 * u tlačítka, které v roce 2026 znamená březen 2027, je zavádějící. Rok se ale nemá
 * OPAKOVAT: v panelu širokém 240 px rozhoduje každý znak o tom, jestli se položka vejde
 * na jeden řádek (viz `HolidaysSection.tsx`).
 *
 * | případ | výstup |
 * | --- | --- |
 * | jeden den | `17. 11. 2027` |
 * | stejný měsíc | `25.–29. 3. 2027` |
 * | stejný rok, jiný měsíc | `29. 1. – 14. 3. 2027` |
 * | přes rok | `23. 12. 2026 – 3. 1. 2027` |
 */
export function formatHolidayWindow(w: HolidayWindow): string {
  if (w.from === w.to) return formatHolidayDay(w.from);
  const [fromYear, fromMonth] = w.from.split("-");
  const [toYear, toMonth] = w.to.split("-");
  if (fromYear !== toYear) return `${formatHolidayDay(w.from)} – ${formatHolidayDay(w.to)}`;
  if (fromMonth === toMonth) return `${Number(w.from.slice(8, 10))}.–${formatHolidayDay(w.to)}`;
  return `${formatDayMonth(w.from)} – ${formatHolidayDay(w.to)}`;
}
